import { hasLoadoutCard } from '../../utils/cardFeatureDetection';

/**
 * Sticky Vitals Bar - Always-Visible HUD with Full Functionality
 * Video Game Interface Pattern + Complete VitalsPanel Integration
 *
 * 3-Row Layout:
 * - Row 1: HP/Stress/Hope with clickable boxes, +/- controls, domain card modes
 * - Row 2: Armor/Evasion + Conditions toggles
 * - Row 3: Traits (Agility, Finesse, Instinct, Presence, Strength, Proficiency)
 *
 * Features:
 * - Fixed position (always visible like video game HUD)
 * - Light in the Dark +1 Hope support
 * - Scar tracking in Hope slots
 * - Domain card modes (Never Upstaged, Spellcharge)
 * - Real-time updates
 * - Responsive (desktop 3-row / mobile compact)
 */

import React, { useState, useRef, useEffect } from 'react';
import { IconHeart, IconZap, IconMinus, IconPlus, IconSword, IconSparkles, IconShield } from '../icons';
import { cn } from '../../lib/utils';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { getElementalTheme, getBoneTheme, getSageTheme } from '../../utils/evasionCalculations';
import { getTotalTraitBonus, getBeastformTraitBonus, getTranscendenceTraitBonus } from '../../utils/characterCalculations';
import toast from 'react-hot-toast';

export default function StickyVitalsBar({
  character,
  companion,
  updateVitals,
  toggleCondition,
  hpDamageMode,
  toggleHPDamageMode,
  currentProficiency,
  evasion,
  armorScore,
  thresholds,
  markArmor,
  spellcastTrait,
  // Conjure Swarm - Beetles mode
  beetlesActive = false,
  setBeetlesActive = null,
  showBeetlesMaintainOption = false,
  setShowBeetlesMaintainOption = null,
}) {
  const isDesktop = useIsDesktop();
  const [isExpanded, setIsExpanded] = useState(false);
  const mobileContainerRef = useRef(null);

  // Ancestry damage mitigation feature reminders (buttons are in AbilitiesPanel Background tab)
  const hasScales = character?.ancestryFeatures?.some(f => f.name === 'Scales');
  const hasThickSkin = character?.ancestryFeatures?.some(f => f.name === 'Thick Skin');

  // Temporary glow states for when Scales/Thick Skin are activated
  const [scalesGlow, setScalesGlow] = useState(false);
  const [thickSkinGlow, setThickSkinGlow] = useState(false);

  // Check for active glow timers (set by AbilitiesPanel when buttons are clicked)
  useEffect(() => {
    const checkGlows = () => {
      const now = Date.now();
      setScalesGlow(character?.scalesActiveUntil > now);
      setThickSkinGlow(character?.thickSkinActiveUntil > now);
    };

    checkGlows();
    const interval = setInterval(checkGlows, 100);
    return () => clearInterval(interval);
  }, [character?.scalesActiveUntil, character?.thickSkinActiveUntil]);

  // Click-outside-to-close for mobile expanded section
  useEffect(() => {
    if (!isExpanded || isDesktop) return;

    const handleClickOutside = (event) => {
      if (mobileContainerRef.current && !mobileContainerRef.current.contains(event.target)) {
        setIsExpanded(false);
        // Don't prevent default - allows tap to interact with underlying element
      }
    };

    // Use mousedown/touchstart to detect before click handlers
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isExpanded, isDesktop]);

  // Extract vitals with safe defaults
  const hp = character?.hp || 0;
  const maxHp = character?.maxHp || 8;
  const stress = character?.stress || 0;
  const maxStress = character?.maxStress || 8;
  const hope = character?.hope || 0;
  const traits = character?.traits || {};
  const conditions = character?.conditions || [];
  const scars = character?.scars || [];

  // Helper to check if a card is in the character's loadout
  const hasCard = (cardKey) => {
    if (!character?.loadout) return false;
    // Loadout may contain full IDs (core_domain_card_xxx) or normalized keys (xxx)
    return character.loadout.some(key => {
      const normalized = key.replace('core_domain_card_', '').replace(/_/g, '-');
      return normalized === cardKey;
    });
  };

  // Passive stat modifier cards
  const hasBareBones = hasCard('bare-bones');
  const hasFortifiedArmor = hasCard('fortified-armor');
  const hasUntouchable = hasCard('untouchable');
  const hasShadowhunter = hasCard('shadowhunter');
  const hasArmorer = hasCard('armorer');
  const hasRiseUp = hasCard('rise-up');

  // Check armor status for conditional cards
  const isArmored = !!character?.armor;

  // Check for Light in the Dark companion upgrade (adds +1 Hope slot)
  const hasLightInTheDark = companion?.levelUpChoices?.some(choice => choice.upgrade === 'light-in-the-dark');
  const maxHopeSlots = hasLightInTheDark ? 7 : 6;

  // Desktop: Full 3-row layout
  if (isDesktop) {
    return (
      <div className="fixed top-[73px] left-0 lg:left-[256px] right-0 z-30 bg-background lg:rounded-tl-2xl overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-start gap-8 w-full">
            {/* Character Vitals */}
            <div className="flex flex-col space-y-3 flex-1 min-w-0">
            {/* Row 1: HP, Stress, Hope */}
            <div className="flex items-center justify-between w-full">
            {/* HP */}
            <div className="flex items-center gap-2">
              <span className={cn(
                "font-bold text-sm flex items-center transition-colors",
                hpDamageMode === 'never-upstaged' ? 'text-orange-300' :
                hpDamageMode === 'spellcharge' ? 'text-purple-300' :
                'text-red-300'
              )}>
                <IconHeart className={cn(
                  "mr-1 w-4 h-4 transition-colors",
                  hpDamageMode === 'never-upstaged' ? 'text-orange-400' :
                  hpDamageMode === 'spellcharge' ? 'text-purple-400' :
                  'text-red-500'
                )}/>
                HP

                {/* Domain Card Mode Icons */}
                {hasLoadoutCard(character, 'never-upstaged') && (
                  <IconSword
                    className={cn(
                      "ml-2 w-4 h-4 cursor-pointer transition-colors",
                      hpDamageMode === 'never-upstaged'
                        ? 'text-orange-300 bg-orange-900/30 rounded p-0.5'
                        : 'text-orange-400 hover:text-orange-300'
                    )}
                    title="Never Upstaged: Gain tokens when taking damage"
                    onClick={() => toggleHPDamageMode('never-upstaged')}
                  />
                )}
                {hasLoadoutCard(character, 'spellcharge') && (
                  <IconSparkles
                    className={cn(
                      "ml-1 w-4 h-4 cursor-pointer transition-colors",
                      hpDamageMode === 'spellcharge'
                        ? 'text-purple-300 bg-purple-900/30 rounded p-0.5'
                        : 'text-purple-400 hover:text-purple-300'
                    )}
                    title="Spellcharge: Gain tokens from magic damage"
                    onClick={() => toggleHPDamageMode('spellcharge')}
                  />
                )}
              </span>

              <button
                onClick={() => updateVitals('hp', hp - 1)}
                className="p-0.5 rounded bg-surface-2 text-gray-300 hover:bg-red-900 hover:text-white border border-border transition-colors"
              >
                <IconMinus className="w-3 h-3"/>
              </button>

              <div className="flex gap-0.5">
                {Array.from({ length: maxHp }, (_, i) => {
                  const getBoxColors = () => {
                    if (hpDamageMode === 'never-upstaged') {
                      return {
                        border: 'border-orange-600',
                        filled: 'bg-orange-600',
                        empty: 'bg-gray-800 border-orange-800'
                      };
                    } else if (hpDamageMode === 'spellcharge') {
                      return {
                        border: 'border-purple-600',
                        filled: 'bg-purple-600',
                        empty: 'bg-gray-800 border-purple-800'
                      };
                    } else {
                      return {
                        border: 'border-red-600',
                        filled: 'bg-red-600',
                        empty: 'bg-gray-800 border-red-800'
                      };
                    }
                  };

                  const colors = getBoxColors();

                  return (
                    <div
                      key={i}
                      className={cn(
                        "w-3 h-3 border rounded-sm transition-colors",
                        colors.border,
                        i < hp ? colors.filled : colors.empty
                      )}
                      title={`HP ${i + 1}/${maxHp}`}
                    />
                  );
                })}
              </div>

              <button
                onClick={() => updateVitals('hp', hp + 1)}
                className="p-0.5 rounded bg-surface-2 text-gray-300 hover:bg-red-900 hover:text-white border border-border transition-colors"
              >
                <IconPlus className="w-3 h-3"/>
              </button>
            </div>

            {/* Stress */}
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm flex items-center text-yellow-400">
                <IconZap className="mr-1 w-4 h-4"/> Stress
              </span>

              <button
                onClick={() => updateVitals('stress', stress - 1)}
                className="p-0.5 rounded bg-surface-2 text-gray-300 hover:bg-red-900 hover:text-white border border-border transition-colors"
              >
                <IconMinus className="w-3 h-3"/>
              </button>

              <div className="flex gap-0.5">
                {Array.from({ length: maxStress }, (_, i) => (
                  <div
                    key={i}
                    className={cn(
                      "w-3 h-3 border border-yellow-600 rounded-sm",
                      i < stress ? 'bg-yellow-600' : 'bg-gray-800 border-yellow-800'
                    )}
                    title={`Stress ${i + 1}/${maxStress}`}
                  />
                ))}
              </div>

              <button
                onClick={() => updateVitals('stress', stress + 1)}
                className="p-0.5 rounded bg-surface-2 text-gray-300 hover:bg-red-900 hover:text-white border border-border transition-colors"
              >
                <IconPlus className="w-3 h-3"/>
              </button>
            </div>

            {/* Hope */}
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm flex items-center text-blue-400">
                <IconSparkles className="mr-1 w-4 h-4"/> Hope
                {hasLightInTheDark && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-indigo-800 text-indigo-200 rounded-full border border-indigo-600" title="Light in the Dark: +1 Hope">
                    +1
                  </span>
                )}
              </span>

              <button
                onClick={() => updateVitals('hope', hope - 1)}
                className="p-0.5 rounded bg-surface-2 text-gray-300 hover:bg-red-900 hover:text-white border border-border transition-colors"
              >
                <IconMinus className="w-3 h-3"/>
              </button>

              <div className="flex gap-0.5">
                {Array.from({ length: maxHopeSlots }, (_, i) => {
                  // Map scars to rightmost slots
                  const scarIndex = scars.length > 0 ? scars.findIndex((_, scarIdx) => scarIdx === (i - (maxHopeSlots - scars.length))) : -1;
                  const scar = scarIndex >= 0 ? scars[scarIndex] : null;
                  const isScarred = !!scar;
                  const isFilled = i < hope && !isScarred;
                  const isBonusSlot = i >= 6; // 7th slot from Light in the Dark

                  return (
                    <div
                      key={i}
                      className={cn(
                        "w-3 h-3 border rounded-sm relative",
                        isScarred
                          ? 'border-red-700 bg-red-900'
                          : isBonusSlot && !isFilled
                            ? 'border-indigo-600 bg-gray-800'
                          : isBonusSlot && isFilled
                            ? 'border-indigo-500 bg-indigo-600'
                          : isFilled
                            ? 'border-blue-500 bg-blue-600'
                            : 'border-blue-700 bg-gray-800'
                      )}
                      title={
                        isScarred
                          ? `Hope Slot ${i + 1}: Scarred (${scar.description})`
                          : isBonusSlot
                            ? `Hope ${i + 1}/${maxHopeSlots} (Light in the Dark)`
                            : `Hope ${i + 1}/${maxHopeSlots}`
                      }
                    >
                      {isScarred && (
                        <div className="absolute inset-0 flex items-center justify-center text-red-300 text-[8px] font-bold leading-none">
                          ✗
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => updateVitals('hope', hope + 1)}
                className="p-0.5 rounded bg-surface-2 text-gray-300 hover:bg-red-900 hover:text-white border border-border transition-colors"
              >
                <IconPlus className="w-3 h-3"/>
              </button>
            </div>
          </div>

          {/* Row 2: Armor, Evasion, Damage Thresholds, Conditions */}
          <div className="flex items-center justify-between w-full">
            {/* Armor Score with Shields */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">Armor</span>
              {/* Passive stat card indicators */}
              {hasArmorer && isArmored && (
                <span title="Armorer: +1 Armor Score (included)" className="text-xs">🛡️</span>
              )}
              {hasBareBones && !isArmored && (
                <span title={`Bare Bones: Armor ${3 + (traits.Strength || 0)} (3 + Strength)`} className="text-xs">💪</span>
              )}
              <div className="flex items-center gap-0.5">
                {/* Base armor shields (blue) */}
                {Array.from({ length: armorScore }, (_, i) => (
                  <IconShield
                    key={i}
                    onClick={() => markArmor && markArmor(i)}
                    className={cn(
                      "w-4 h-4 cursor-pointer transition-colors",
                      (character?.armorMarks || []).includes(i)
                        ? 'text-red-500 fill-red-200'
                        : 'text-blue-400 hover:text-blue-300'
                    )}
                    title={`Armor ${i + 1}/${armorScore}`}
                  />
                ))}
                {/* Temporary armor shields (gold) */}
                {character?.temporaryArmor?.amount > 0 && Array.from({ length: character.temporaryArmor.amount }, (_, i) => (
                  <IconShield
                    key={`temp-${i}`}
                    onClick={() => markArmor && markArmor(armorScore + i)}
                    className={cn(
                      "w-4 h-4 cursor-pointer transition-colors",
                      (character?.armorMarks || []).includes(armorScore + i)
                        ? 'text-red-500 fill-red-200'
                        : 'text-yellow-400 hover:text-yellow-300'
                    )}
                    title={`Temporary Armor (${character.temporaryArmor.grantedByName || 'Tava\'s Armor'})`}
                  />
                ))}
              </div>
            </div>

            {/* Evasion */}
            {(() => {
              const naturalBoost = character?.naturalEvasionBoost;
              const iSeeItBoost = character?.iSeeItComingBoost;
              const giftedTrackerBoost = character?.giftedTrackerActive ? 1 : 0;
              const battleBondedBoost = character?.battleBondedActive ? 2 : 0;
              const roguesDodgeBoost = character?.roguesDodgeActive ? 2 : 0;
              // Temporary boosts shown as "+ X" format
              const temporaryBoost = (naturalBoost || 0) + (iSeeItBoost || 0) + giftedTrackerBoost;
              // Battle-Bonded and Rogue's Dodge are persistent when active, add directly to display value
              const displayEvasion = (evasion || 0) + battleBondedBoost + roguesDodgeBoost;
              const elementalTheme = naturalBoost ? getElementalTheme(character.elementalistElement) : null;
              const boneTheme = iSeeItBoost ? getBoneTheme() : null;
              const sageTheme = giftedTrackerBoost ? getSageTheme() : null;
              const hasNimble = character?.ancestryFeatures?.some(f => f.name === 'Nimble');

              // Determine which theme to use for styling (only for temporary boosts that show as "+ X")
              const activeTheme = elementalTheme || boneTheme || sageTheme;

              return (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">Evasion</span>
                  {/* Simiah Nimble indicator (permanent +1, already in base value) */}
                  {hasNimble && <span title="Simiah Nimble: +1 Evasion (included)">🐒</span>}
                  {/* Untouchable indicator (permanent +Agility/2, already in base value) */}
                  {hasUntouchable && <span title={`Untouchable: +${Math.floor((traits.Agility || 0) / 2)} Evasion (included)`}>👻</span>}
                  {/* Shadowhunter indicator (permanent +1, already in base value) */}
                  {hasShadowhunter && <span title="Shadowhunter: +1 Evasion (in darkness)">🌙</span>}
                  {/* Battle-Bonded indicator (passive +2 when companion in melee) */}
                  {battleBondedBoost > 0 && <span title="Battle-Bonded: +2 Evasion (included)">🐾</span>}
                  {/* Rogue's Dodge indicator (+2 until hit or rest) */}
                  {roguesDodgeBoost > 0 && <span title="Rogue's Dodge: +2 Evasion (until hit or rest)">🗡️</span>}
                  <span
                    className={cn(
                      "text-sm font-bold transition-all",
                      temporaryBoost > 0
                        ? `${activeTheme?.primary || 'text-blue-400'} ${activeTheme?.glow || ''} scale-110`
                        : 'text-gray-300'
                    )}
                  >
                    {temporaryBoost > 0
                      ? `${displayEvasion} + ${temporaryBoost}`
                      : displayEvasion}
                  </span>
                  {/* Show source icons for temporary boosts */}
                  {naturalBoost > 0 && <span title={`Natural Evasion +${naturalBoost}`}>{elementalTheme?.icon}</span>}
                  {iSeeItBoost > 0 && <span title={`I See It Coming +${iSeeItBoost}`}>{boneTheme?.icon}</span>}
                  {giftedTrackerBoost > 0 && <span title="Gifted Tracker +1">🎯</span>}
                </div>
              );
            })()}

            {/* Divider */}
            <div className="w-px h-6 bg-border" aria-hidden="true" />

            {/* Damage Thresholds - Glows when Beetles/Scales/ThickSkin are active */}
            <div
              className={cn(
                "flex items-center gap-2 text-xs px-2 py-1 rounded transition-all duration-300",
                beetlesActive && !showBeetlesMaintainOption && "ring-2 ring-emerald-500/50 bg-emerald-900/20"
              )}
              title={beetlesActive ? "🪲 Armored Beetles: Next damage severity reduced by 1 threshold" : undefined}
            >
              {/* Beetle icon when active */}
              {beetlesActive && !showBeetlesMaintainOption && (
                <span className="text-emerald-400">🪲</span>
              )}
              {/* Fortified Armor indicator (+2 all thresholds when armored) */}
              {hasFortifiedArmor && isArmored && (
                <span title="Fortified Armor: +2 to all thresholds (included)">🏰</span>
              )}

              {/* Minor - glows amber when Thick Skin is activated */}
              <span className={cn(
                "font-medium transition-all duration-300 px-1 rounded",
                thickSkinGlow
                  ? "text-amber-200 ring-2 ring-amber-500/50 bg-amber-900/30"
                  : beetlesActive && !showBeetlesMaintainOption
                    ? "text-emerald-300"
                    : "text-yellow-400"
              )}>
                {hasThickSkin && <span title="Thick Skin: 2 Stress → skip 1 HP">⛏️</span>}
                Minor
              </span>
              <span className="text-gray-600">|</span>
              <span className={cn(
                "font-bold transition-colors",
                thickSkinGlow ? "text-amber-200" :
                beetlesActive && !showBeetlesMaintainOption ? "text-emerald-200" : "text-gray-100"
              )}>{thresholds?.minor || 0}</span>
              <span className="text-gray-600">|</span>

              {/* Major */}
              <span className={cn(
                "font-medium transition-colors",
                beetlesActive && !showBeetlesMaintainOption ? "text-emerald-300" : "text-orange-400"
              )}>Major</span>
              <span className="text-gray-600">|</span>
              <span className={cn(
                "font-bold transition-colors",
                beetlesActive && !showBeetlesMaintainOption ? "text-emerald-200" : "text-gray-100"
              )}>{thresholds?.major || 0}</span>
              <span className="text-gray-600">|</span>

              {/* Severe - glows emerald when Scales is activated */}
              <span className={cn(
                "font-medium transition-all duration-300 px-1 rounded",
                scalesGlow
                  ? "text-emerald-200 ring-2 ring-emerald-500/50 bg-emerald-900/30"
                  : beetlesActive && !showBeetlesMaintainOption
                    ? "text-emerald-300"
                    : "text-red-400"
              )}>
                Severe
                {hasScales && <span title="Scales: 1 Stress → -1 HP">🐲</span>}
                {hasRiseUp && <span title={`Rise Up: +${currentProficiency || 0} to Severe (included)`}>⬆️</span>}
              </span>
            </div>

            {/* Conjure Swarm - Spend Hope to Maintain Option (appears after damage) */}
            {showBeetlesMaintainOption && (
              <div className="flex items-center gap-1">
                <span className="px-2 py-1 text-xs rounded border bg-emerald-900/30 text-emerald-300 border-emerald-600">
                  🪲 Protected!
                </span>
                <button
                  onClick={() => {
                    const currentHope = character?.hope || 0;
                    if (currentHope > 0) {
                      updateVitals('hope', currentHope - 1);
                      setShowBeetlesMaintainOption?.(false);
                      toast.success('🪲 Beetles maintained! (-1 Hope)', { duration: 2500 });
                    } else {
                      toast.error('Not enough Hope to maintain beetles', { duration: 2000 });
                      setBeetlesActive?.(false);
                      setShowBeetlesMaintainOption?.(false);
                    }
                  }}
                  disabled={(character?.hope || 0) < 1}
                  className={cn(
                    "px-2 py-1 text-xs rounded font-semibold transition-all",
                    (character?.hope || 0) >= 1
                      ? "bg-emerald-700 text-emerald-200 border border-emerald-500 hover:bg-emerald-600"
                      : "bg-gray-700 text-gray-500 cursor-not-allowed border border-gray-600"
                  )}
                >
                  Keep (1💎)
                </button>
                <button
                  onClick={() => {
                    setBeetlesActive?.(false);
                    setShowBeetlesMaintainOption?.(false);
                    toast('🪲 Beetles dispersed.', { duration: 2000 });
                  }}
                  className="px-2 py-1 text-xs rounded font-semibold bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600 transition-all"
                >
                  ✕
                </button>
              </div>
            )}

            {/* Divider */}
            <div className="w-px h-6 bg-border" aria-hidden="true" />

            {/* Conditions */}
            <div className="flex items-center gap-2">
              {['Hidden', 'Restrained', 'Vulnerable', 'Dazed'].map(condition => (
                <button
                  key={condition}
                  onClick={() => toggleCondition(condition)}
                  className={cn(
                    "px-2 py-1 text-xs rounded border transition-all",
                    conditions.includes(condition)
                      ? 'bg-red-900 text-red-200 border-red-600'
                      : 'bg-surface-2 text-gray-400 border-border hover:bg-red-900/50 hover:text-white'
                  )}
                >
                  {condition}
                </button>
              ))}
              {/* Galapa Retract Status Badge */}
              {character?.isRetracted && (
                <span
                  className="px-2 py-1 text-xs rounded border bg-stone-800 text-stone-200 border-stone-600 animate-pulse"
                  title="Retracted: Physical damage halved, disadvantage on actions, cannot move"
                >
                  🐢 Shell
                </span>
              )}
            </div>
          </div>

          {/* Row 3: Traits */}
          <div className="flex items-center justify-between w-full text-sm">
            {Object.entries(traits)
              .filter(([trait]) => trait !== spellcastTrait) // Filter out spellcast trait to avoid duplication
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([trait, value]) => {
                const traitBonus = getTotalTraitBonus(character, trait);
                const totalValue = value + traitBonus;
                const hasBeastformBonus = getBeastformTraitBonus(character, trait) > 0;
                const hasTranscendenceBonus = getTranscendenceTraitBonus(character, trait) > 0;
                const hasAnyBonus = hasBeastformBonus || hasTranscendenceBonus;
                return (
                  <div key={trait} className="flex items-center gap-2">
                    <span className="text-gray-400 font-medium">{trait}</span>
                    <span className={cn(
                      "font-bold",
                      hasAnyBonus ? "text-green-400" : "text-yellow-400"
                    )}>
                      {totalValue >= 0 ? `+${totalValue}` : totalValue}
                      {hasBeastformBonus && <span className="text-xs text-green-300 ml-1">🐾</span>}
                      {hasTranscendenceBonus && <span className="text-xs text-purple-300 ml-1">✨</span>}
                    </span>
                  </div>
                );
              })}
            <div className="w-px h-5 bg-border" aria-hidden="true" />
            <div className="flex items-center gap-2">
              <span className="text-purple-300 font-medium">Proficiency</span>
              <span className="text-purple-400 font-bold">{currentProficiency || 0}</span>
            </div>
            {spellcastTrait && (() => {
              const spellcastBonus = getTotalTraitBonus(character, spellcastTrait);
              const spellcastTotal = (traits[spellcastTrait] || 0) + spellcastBonus;
              const hasSpellcastBeastformBonus = getBeastformTraitBonus(character, spellcastTrait) > 0;
              const hasSpellcastTranscendenceBonus = getTranscendenceTraitBonus(character, spellcastTrait) > 0;
              const hasAnySpellcastBonus = hasSpellcastBeastformBonus || hasSpellcastTranscendenceBonus;
              return (
                <>
                  <div className="w-px h-5 bg-border" aria-hidden="true" />
                  <div className="flex items-center gap-2">
                    <span className="text-blue-300 font-medium">Spellcast ({spellcastTrait})</span>
                    <span className={cn(
                      "font-bold",
                      hasAnySpellcastBonus ? "text-green-400" : "text-blue-400"
                    )}>
                      {spellcastTotal >= 0 ? `+${spellcastTotal}` : spellcastTotal}
                      {hasSpellcastBeastformBonus && <span className="text-xs text-green-300 ml-1">🐾</span>}
                      {hasSpellcastTranscendenceBonus && <span className="text-xs text-purple-300 ml-1">✨</span>}
                    </span>
                  </div>
                </>
              );
            })()}
          </div>
          </div>

            {/* Companion Vitals - Only show if companion exists */}
            {companion && (
              <div className="flex flex-col space-y-2 border-l border-border pl-6 flex-shrink-0">
                {/* Companion Name */}
                <div className="text-sm font-bold text-green-400 mb-1">
                  {companion.name || 'Companion'}
                </div>

                {/* Companion Stress */}
                <div className="flex items-center gap-2">
                  <span className="font-bold text-xs text-yellow-300 flex items-center">
                    <IconZap className="mr-1 w-3 h-3 text-yellow-400"/> STR
                  </span>
                  <button
                    onClick={() => updateVitals('companionStress', (companion.stress || 0) - 1, companion)}
                    className="p-0.5 rounded bg-surface-2 text-gray-300 hover:bg-red-900 hover:text-white border border-border transition-colors"
                  >
                    <IconMinus className="w-3 h-3"/>
                  </button>
                  <div className="flex gap-0.5">
                    {Array.from({ length: companion.maxStress || 4 }, (_, i) => (
                      <div
                        key={i}
                        className={cn(
                          "w-3 h-3 border border-yellow-600 rounded-sm",
                          i < (companion.stress || 0) ? 'bg-yellow-600' : 'bg-gray-800 border-yellow-800'
                        )}
                        title={`Stress ${i + 1}/${companion.maxStress}`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={() => updateVitals('companionStress', (companion.stress || 0) + 1, companion)}
                    className="p-0.5 rounded bg-surface-2 text-gray-300 hover:bg-yellow-900 hover:text-white border border-border transition-colors"
                  >
                    <IconPlus className="w-3 h-3"/>
                  </button>
                </div>

                {/* Companion Evasion */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">Evasion</span>
                  <span className="text-xs font-bold text-blue-400">{companion.evasion || 0}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Mobile: Compact layout with expandable dropdown
  return (
    <div ref={mobileContainerRef} className="fixed top-[73px] left-0 right-0 z-30 bg-background">
      <div className="max-w-7xl mx-auto">
        {/* Character Name Header */}
        <div className="px-4 pt-3 pb-2 border-b border-border">
        <h2 className="text-lg font-serif font-bold text-red-400 truncate">
          {character?.name || 'Character'}
        </h2>
      </div>

      {/* Compact Bar (always visible) */}
      <button
        onClick={(e) => {
          e.stopPropagation(); // Prevent parent card's onClick from firing
          setIsExpanded(!isExpanded);
        }}
        className="w-full flex items-center justify-around px-4 py-2 gap-2 hover:bg-surface-2 transition-colors"
      >
        <div className="flex items-center gap-1">
          <IconHeart className="w-4 h-4 text-red-400" />
          <span className="text-sm font-bold text-red-400">{hp}/{maxHp}</span>
        </div>
        <div className="w-px h-6 bg-border" aria-hidden="true" />
        <div className="flex items-center gap-1">
          <IconZap className="w-4 h-4 text-yellow-400" />
          <span className="text-sm font-bold text-yellow-400">{stress}/{maxStress}</span>
        </div>
        <div className="w-px h-6 bg-border" aria-hidden="true" />
        <div className="flex items-center gap-1">
          <IconSparkles className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-bold text-blue-400">{hope}/{maxHopeSlots}</span>
        </div>
        <div className="w-px h-6 bg-border" aria-hidden="true" />
        <div className="flex items-center gap-1">
          <IconShield className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-bold text-blue-400">{armorScore}</span>
          {character?.temporaryArmor?.amount > 0 && (
            <span className="text-sm font-bold text-yellow-400">+{character.temporaryArmor.amount}</span>
          )}
        </div>
        {/* Expand/Collapse Indicator */}
        <svg
          className={cn("w-4 h-4 text-gray-400 transition-transform", isExpanded && "rotate-180")}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Companion Vitals Row (mobile) - Second row for Beastbound Rangers */}
      {companion && (
        <div className="flex items-center justify-around px-4 py-1.5 gap-2 border-t border-border">
          <div className="flex items-center gap-1">
            <span className="text-xs font-bold text-green-400">{companion.name || 'Companion'}</span>
          </div>
          <div className="w-px h-6 bg-border" aria-hidden="true" />
          <div className="flex items-center gap-1">
            <IconZap className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-bold text-yellow-400">{companion.stress || 0}/{companion.maxStress || 4}</span>
          </div>
          <div className="w-px h-6 bg-border" aria-hidden="true" />
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400">Evasion</span>
            <span className="text-sm font-bold text-blue-400">{companion.evasion || 0}</span>
          </div>
        </div>
      )}

      {/* Expanded Full Controls (dropdown) */}
      {isExpanded && (
        <div className="border-t border-border px-4 py-4 space-y-4 bg-surface-2">
          {/* HP Controls */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={cn(
                "font-bold text-sm flex items-center transition-colors",
                hpDamageMode === 'never-upstaged' ? 'text-orange-300' :
                hpDamageMode === 'spellcharge' ? 'text-purple-300' :
                'text-red-300'
              )}>
                <IconHeart className={cn(
                  "mr-2 w-5 h-5 transition-colors",
                  hpDamageMode === 'never-upstaged' ? 'text-orange-400' :
                  hpDamageMode === 'spellcharge' ? 'text-purple-400' :
                  'text-red-500'
                )}/>
                HP

                {/* Domain Card Mode Icons */}
                {hasLoadoutCard(character, 'never-upstaged') && (
                  <IconSword
                    className={cn(
                      "ml-2 w-5 h-5 cursor-pointer transition-colors",
                      hpDamageMode === 'never-upstaged'
                        ? 'text-orange-300 bg-orange-900/30 rounded p-0.5'
                        : 'text-orange-400 hover:text-orange-300'
                    )}
                    title="Never Upstaged"
                    onClick={(e) => { e.stopPropagation(); toggleHPDamageMode('never-upstaged'); }}
                  />
                )}
                {hasLoadoutCard(character, 'spellcharge') && (
                  <IconSparkles
                    className={cn(
                      "ml-1 w-5 h-5 cursor-pointer transition-colors",
                      hpDamageMode === 'spellcharge'
                        ? 'text-purple-300 bg-purple-900/30 rounded p-0.5'
                        : 'text-purple-400 hover:text-purple-300'
                    )}
                    title="Spellcharge"
                    onClick={(e) => { e.stopPropagation(); toggleHPDamageMode('spellcharge'); }}
                  />
                )}

                {/* Character Status Indicators */}
                {character.status === 'deceased' && (
                  <span className="ml-2 px-2 py-1 text-xs bg-red-900 text-red-200 rounded-full border border-red-700">Deceased</span>
                )}
                {character.status === 'journey_ended' && (
                  <span className="ml-2 px-2 py-1 text-xs bg-gray-800 text-gray-300 rounded-full border border-gray-600">Journey Ended</span>
                )}
                {hp === 0 && character.status !== 'deceased' && character.status !== 'journey_ended' && (
                  <span className="ml-2 px-2 py-1 text-xs bg-red-900 text-red-200 rounded-full animate-pulse border border-red-700">Death Move</span>
                )}
              </span>
              <div className="flex items-center space-x-2">
                <button onClick={() => updateVitals('hp', hp - 1)} className="p-2 rounded-full bg-gray-800 text-gray-300 hover:bg-red-900 hover:text-white border border-gray-600 transition-colors"><IconMinus className="w-4 h-4"/></button>
                <div className="flex space-x-1">
                  {Array.from({ length: maxHp }, (_, i) => {
                    const getBoxColors = () => {
                      if (hpDamageMode === 'never-upstaged') {
                        return { border: 'border-orange-600', filled: 'bg-orange-600', empty: 'bg-gray-800 border-orange-800' };
                      } else if (hpDamageMode === 'spellcharge') {
                        return { border: 'border-purple-600', filled: 'bg-purple-600', empty: 'bg-gray-800 border-purple-800' };
                      } else {
                        return { border: 'border-red-600', filled: 'bg-red-600', empty: 'bg-gray-800 border-red-800' };
                      }
                    };
                    const colors = getBoxColors();
                    return <div key={i} className={cn("w-5 h-5 border-2 rounded-sm transition-colors", colors.border, i < hp ? colors.filled : colors.empty)} />;
                  })}
                </div>
                <button onClick={() => updateVitals('hp', hp + 1)} className="p-2 rounded-full bg-gray-800 text-gray-300 hover:bg-red-900 hover:text-white border border-gray-600 transition-colors"><IconPlus className="w-4 h-4"/></button>
              </div>
            </div>
          </div>

          {/* Stress Controls */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-semibold flex items-center text-yellow-400"><IconZap className="mr-2 w-5 h-5"/> Stress</span>
              <div className="flex items-center space-x-2">
                <button onClick={() => updateVitals('stress', stress - 1)} className="p-2 rounded-full bg-gray-800 text-gray-300 hover:bg-red-900 hover:text-white border border-gray-600 transition-colors"><IconMinus className="w-4 h-4"/></button>
                <div className="flex space-x-1">
                  {Array.from({ length: maxStress }, (_, i) => (
                    <div key={i} className={cn("w-5 h-5 border-2 border-yellow-600 rounded-sm", i < stress ? 'bg-yellow-600' : 'bg-gray-800 border-yellow-800')} />
                  ))}
                </div>
                <button onClick={() => updateVitals('stress', stress + 1)} className="p-2 rounded-full bg-gray-800 text-gray-300 hover:bg-red-900 hover:text-white border border-gray-600 transition-colors"><IconPlus className="w-4 h-4"/></button>
              </div>
            </div>
          </div>

          {/* Hope Controls */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-semibold flex items-center text-blue-400">
                <IconSparkles className="mr-2 w-5 h-5 text-blue-400" /> Hope
                {hasLightInTheDark && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-indigo-800 text-indigo-200 rounded-full border border-indigo-600">+1 ✨</span>
                )}
              </span>
              <div className="flex items-center space-x-2">
                <button onClick={() => updateVitals('hope', hope - 1)} className="p-2 rounded-full bg-gray-800 text-gray-300 hover:bg-red-900 hover:text-white border border-gray-600 transition-colors"><IconMinus className="w-4 h-4"/></button>
                <div className="flex space-x-1">
                  {Array.from({ length: maxHopeSlots }, (_, i) => {
                    const scarIndex = scars.length > 0 ? scars.findIndex((_, scarIdx) => scarIdx === (i - (maxHopeSlots - scars.length))) : -1;
                    const scar = scarIndex >= 0 ? scars[scarIndex] : null;
                    const isScarred = !!scar;
                    const isFilled = i < hope && !isScarred;
                    const isBonusSlot = i >= 6;
                    return (
                      <div key={i} className={cn("w-5 h-5 border-2 rounded-sm relative", isScarred ? 'border-red-700 bg-red-900' : isBonusSlot && !isFilled ? 'border-indigo-600 bg-gray-800' : isBonusSlot && isFilled ? 'border-indigo-500 bg-indigo-600' : isFilled ? 'border-blue-500 bg-blue-600' : 'border-blue-700 bg-gray-800')}>
                        {isScarred && (<div className="absolute inset-0 flex items-center justify-center text-red-300 text-xs font-bold">✗</div>)}
                      </div>
                    );
                  })}
                </div>
                <button onClick={() => updateVitals('hope', hope + 1)} className="p-2 rounded-full bg-gray-800 text-gray-300 hover:bg-red-900 hover:text-white border border-gray-600 transition-colors"><IconPlus className="w-4 h-4"/></button>
              </div>
            </div>
          </div>

          {/* Armor & Evasion */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-400">Armor</span>
              {/* Passive stat card indicators */}
              {hasArmorer && isArmored && (
                <span title="Armorer: +1 Armor Score (included)" className="text-xs">🛡️</span>
              )}
              {hasBareBones && !isArmored && (
                <span title={`Bare Bones: Armor ${3 + (traits.Strength || 0)} (3 + Strength)`} className="text-xs">💪</span>
              )}
              <div className="flex items-center gap-1">
                {/* Base armor shields (blue) */}
                {Array.from({ length: armorScore }, (_, i) => (
                  <IconShield
                    key={i}
                    onClick={() => markArmor && markArmor(i)}
                    className={cn(
                      "w-5 h-5 cursor-pointer transition-colors",
                      (character?.armorMarks || []).includes(i)
                        ? 'text-red-500 fill-red-200'
                        : 'text-blue-400'
                    )}
                  />
                ))}
                {/* Temporary armor shields (gold) */}
                {character?.temporaryArmor?.amount > 0 && Array.from({ length: character.temporaryArmor.amount }, (_, i) => (
                  <IconShield
                    key={`temp-${i}`}
                    onClick={() => markArmor && markArmor(armorScore + i)}
                    className={cn(
                      "w-5 h-5 cursor-pointer transition-colors",
                      (character?.armorMarks || []).includes(armorScore + i)
                        ? 'text-red-500 fill-red-200'
                        : 'text-yellow-400'
                    )}
                  />
                ))}
              </div>
            </div>
            {(() => {
              const naturalBoost = character?.naturalEvasionBoost;
              const iSeeItBoost = character?.iSeeItComingBoost;
              const giftedTrackerBoost = character?.giftedTrackerActive ? 1 : 0;
              const battleBondedBoost = character?.battleBondedActive ? 2 : 0;
              const roguesDodgeBoost = character?.roguesDodgeActive ? 2 : 0;
              // Temporary boosts shown as "+ X" format
              const temporaryBoost = (naturalBoost || 0) + (iSeeItBoost || 0) + giftedTrackerBoost;
              // Battle-Bonded and Rogue's Dodge are passive when active, add directly to display value
              const displayEvasion = (evasion || 0) + battleBondedBoost + roguesDodgeBoost;
              const elementalTheme = naturalBoost ? getElementalTheme(character.elementalistElement) : null;
              const boneTheme = iSeeItBoost ? getBoneTheme() : null;
              const activeTheme = elementalTheme || boneTheme || (giftedTrackerBoost ? getSageTheme() : null);
              const hasNimble = character?.ancestryFeatures?.some(f => f.name === 'Nimble');

              return (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">Evasion</span>
                  {hasNimble && <span title="Simiah Nimble: +1 Evasion (included)">🐒</span>}
                  {hasUntouchable && <span title={`Untouchable: +${Math.floor((traits.Agility || 0) / 2)} Evasion (included)`}>👻</span>}
                  {hasShadowhunter && <span title="Shadowhunter: +1 Evasion (in darkness)">🌙</span>}
                  {battleBondedBoost > 0 && <span title="Battle-Bonded: +2 Evasion (included)">🐾</span>}
                  {roguesDodgeBoost > 0 && <span title="Rogue's Dodge: +2 Evasion (until hit or rest)">🗡️</span>}
                  <span
                    className={cn(
                      "font-bold transition-all",
                      temporaryBoost > 0
                        ? `${activeTheme?.primary || 'text-blue-400'} ${activeTheme?.glow || ''} scale-110`
                        : 'text-gray-300'
                    )}
                  >
                    {temporaryBoost > 0
                      ? `${displayEvasion} + ${temporaryBoost}`
                      : displayEvasion}
                  </span>
                  {naturalBoost > 0 && <span>{elementalTheme?.icon}</span>}
                  {iSeeItBoost > 0 && <span>{boneTheme?.icon}</span>}
                  {giftedTrackerBoost > 0 && <span>🎯</span>}
                </div>
              );
            })()}
          </div>

          {/* Damage Thresholds - with ancestry feature glows */}
          <div className="flex items-center justify-center gap-2 text-sm py-2 bg-gray-900 rounded-lg border border-gray-700">
            {/* Fortified Armor indicator */}
            {hasFortifiedArmor && isArmored && (
              <span title="Fortified Armor: +2 to all thresholds (included)">🏰</span>
            )}
            {/* Minor - glows amber when Thick Skin is activated */}
            <span className={cn(
              "font-medium transition-all duration-300 px-1 rounded",
              thickSkinGlow
                ? "text-amber-200 ring-2 ring-amber-500/50 bg-amber-900/30"
                : "text-yellow-400"
            )}>
              {hasThickSkin && <span title="Thick Skin: 2 Stress → skip 1 HP">⛏️</span>}
              Minor
            </span>
            <span className="text-gray-600">|</span>
            <span className={cn(
              "font-bold transition-colors",
              thickSkinGlow ? "text-amber-200" : "text-gray-100"
            )}>{thresholds?.minor || 0}</span>
            <span className="text-gray-600">|</span>
            <span className="text-orange-400 font-medium">Major</span>
            <span className="text-gray-600">|</span>
            <span className="text-gray-100 font-bold">{thresholds?.major || 0}</span>
            <span className="text-gray-600">|</span>
            {/* Severe - glows emerald when Scales is activated */}
            <span className={cn(
              "font-medium transition-all duration-300 px-1 rounded",
              scalesGlow
                ? "text-emerald-200 ring-2 ring-emerald-500/50 bg-emerald-900/30"
                : "text-red-400"
            )}>
              Severe
              {hasScales && <span title="Scales: 1 Stress → -1 HP">🐲</span>}
              {hasRiseUp && <span title={`Rise Up: +${currentProficiency || 0} to Severe (included)`}>⬆️</span>}
            </span>
          </div>

          {/* Conditions */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-400">Conditions</h4>
            <div className="flex flex-wrap gap-1.5">
              {['Hidden', 'Restrained', 'Vulnerable', 'Dazed'].map(condition => (
                <button
                  key={condition}
                  onClick={() => toggleCondition(condition)}
                  className={cn(
                    "px-2.5 py-1.5 text-sm rounded-lg border transition-all",
                    conditions.includes(condition)
                      ? 'bg-red-900 text-red-200 border-red-600'
                      : 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-red-900/50'
                  )}
                >
                  {condition}
                </button>
              ))}
              {/* Galapa Retract Status Badge */}
              {character?.isRetracted && (
                <span
                  className="px-2.5 py-1.5 text-sm rounded-lg border bg-stone-800 text-stone-200 border-stone-600 animate-pulse"
                  title="Retracted: Physical damage halved, disadvantage on actions, cannot move"
                >
                  🐢 Shell
                </span>
              )}
            </div>
          </div>

          {/* Traits Row */}
          <div className="border-t border-border pt-3">
            <div className="flex flex-wrap gap-2 justify-center">
              {Object.entries(traits)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([trait, value]) => {
                  const traitBonus = getTotalTraitBonus(character, trait);
                  const totalValue = value + traitBonus;
                  const hasBeastformBonus = getBeastformTraitBonus(character, trait) > 0;
                  const hasTranscendenceBonus = getTranscendenceTraitBonus(character, trait) > 0;
                  const hasAnyBonus = hasBeastformBonus || hasTranscendenceBonus;
                  return (
                    <div key={trait} className={cn(
                      "flex items-center gap-1 px-2 py-1 rounded",
                      hasAnyBonus ? "bg-green-900/50 border border-green-700" : "bg-gray-700"
                    )}>
                      <span className="text-gray-400 text-sm">{trait}</span>
                      <span className={cn(
                        "font-bold text-sm",
                        hasAnyBonus ? "text-green-400" : "text-yellow-400"
                      )}>
                        {totalValue >= 0 ? `+${totalValue}` : totalValue}
                        {hasBeastformBonus && <span className="text-xs ml-0.5">🐾</span>}
                        {hasTranscendenceBonus && <span className="text-xs ml-0.5">✨</span>}
                      </span>
                    </div>
                  );
                })}
              {/* Proficiency */}
              <div className="flex items-center gap-1 bg-purple-900/50 px-2 py-1 rounded border border-purple-700">
                <span className="text-purple-300 text-sm">Proficiency</span>
                <span className="text-purple-400 font-bold text-sm">{currentProficiency || 0}</span>
              </div>
              {/* Spellcast if applicable */}
              {spellcastTrait && (() => {
                const spellcastBonus = getTotalTraitBonus(character, spellcastTrait);
                const spellcastTotal = (traits[spellcastTrait] || 0) + spellcastBonus;
                const hasSpellcastBeastformBonus = getBeastformTraitBonus(character, spellcastTrait) > 0;
                const hasSpellcastTranscendenceBonus = getTranscendenceTraitBonus(character, spellcastTrait) > 0;
                const hasAnySpellcastBonus = hasSpellcastBeastformBonus || hasSpellcastTranscendenceBonus;
                return (
                  <div className={cn(
                    "flex items-center gap-1 px-2 py-1 rounded",
                    hasAnySpellcastBonus ? "bg-green-900/50 border border-green-700" : "bg-blue-900/50 border border-blue-700"
                  )}>
                    <span className="text-blue-300 text-sm">Spellcast</span>
                    <span className={cn(
                      "font-bold text-sm",
                      hasAnySpellcastBonus ? "text-green-400" : "text-blue-400"
                    )}>
                      {spellcastTotal >= 0 ? `+${spellcastTotal}` : spellcastTotal}
                      {hasSpellcastBeastformBonus && <span className="text-xs ml-0.5">🐾</span>}
                      {hasSpellcastTranscendenceBonus && <span className="text-xs ml-0.5">✨</span>}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
